import { useCallback, useEffect, useState } from 'react'
import { get, patch, post } from 'aws-amplify/api'
import { useAuth } from '@/lib/auth/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

type TargetEnvironment = 'dev' | 'uat' | 'prod'
type GifEntry = { id: string; url: string; enabled: boolean }
type ManifestResponse = {
  targetEnvironment: TargetEnvironment
  etag: string
  updatedAt: string | null
  manifest: { version: 1; updatedAt?: string; gifs: GifEntry[] }
}

const environments: TargetEnvironment[] = ['dev', 'uat', 'prod']

const parseResponse = async (operation: { response: Promise<{ body: { json: () => Promise<unknown> } }> }) => {
  const response = await operation.response
  return response.body.json()
}

export default function ClubroomGifsPage() {
  const { isAuthenticated, session } = useAuth()
  const [targetEnvironment, setTargetEnvironment] = useState<TargetEnvironment>('uat')
  const [data, setData] = useState<ManifestResponse | null>(null)
  const [draftGifs, setDraftGifs] = useState<GifEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const requestOptions = useCallback(() => ({
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.tokens?.idToken?.toString() ?? ''}`,
    },
  }), [session?.tokens?.idToken])

  const loadManifest = useCallback(async () => {
    if (!isAuthenticated || session?.tokens?.idToken == null) return
    setLoading(true)
    setError(null)
    setNotice(null)
    try {
      const result = await parseResponse(get({
        apiName: 'dashboard',
        path: `/dashboard/admin/clubroom/gifs/${targetEnvironment}`,
        options: requestOptions(),
      })) as ManifestResponse
      setData(result)
      setDraftGifs(result.manifest.gifs)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load GIF manifest.')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, requestOptions, session?.tokens?.idToken, targetEnvironment])

  useEffect(() => { void loadManifest() }, [loadManifest])

  const saveManifest = async () => {
    if (data == null) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const result = await parseResponse(patch({
        apiName: 'dashboard',
        path: `/dashboard/admin/clubroom/gifs/${targetEnvironment}`,
        options: {
          ...requestOptions(),
          body: {
            ifMatchEtag: data.etag,
            gifs: draftGifs.map(({ id, enabled }) => ({ id, enabled })),
            reason: 'Admin updated Clubroom GIF availability',
          },
        },
      })) as ManifestResponse
      setData(result)
      setDraftGifs(result.manifest.gifs)
      setNotice('GIF availability saved.')
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Could not save GIF manifest.'
      setError(message)
      if (message.includes('409')) setNotice('Manifest changed elsewhere. Reload before saving again.')
    } finally {
      setSaving(false)
    }
  }

  const regenerateManifest = async () => {
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      await parseResponse(post({
        apiName: 'dashboard',
        path: `/dashboard/admin/clubroom/gifs/${targetEnvironment}/regenerate`,
        options: {
          ...requestOptions(),
          body: { reason: 'Admin regenerated Clubroom GIF manifest' },
        },
      }))
      await loadManifest()
      setNotice('Manifest regenerated. New or changed GIFs start disabled.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not regenerate GIF manifest.')
    } finally {
      setSaving(false)
    }
  }

  const toggleGif = (id: string) => setDraftGifs((current) => current.map((gif) =>
    gif.id === id ? { ...gif, enabled: !gif.enabled } : gif,
  ))

  return (
    <main className="mx-auto w-full max-w-6xl space-y-6 p-6 text-slate-100">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-300">Clubroom</p>
          <h1 className="mt-2 text-3xl font-bold">Image library</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Review catalog images and choose which images Fremantle supporters can post.
          </p>
        </div>
        <label className="flex flex-col gap-2 text-sm text-slate-300">
          Environment
          <select
            className="min-w-36 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={targetEnvironment}
            onChange={(event) => setTargetEnvironment(event.target.value as TargetEnvironment)}
          >
            {environments.map((environment) => <option key={environment} value={environment}>{environment.toUpperCase()}</option>)}
          </select>
        </label>
      </div>

      <Card className="border-slate-800 bg-slate-950/70 text-slate-100">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Available images</CardTitle>
            <p className="mt-1 text-sm text-slate-400">
              {draftGifs.filter((gif) => gif.enabled).length} enabled · {draftGifs.length} catalogued
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => void loadManifest()} disabled={loading || saving}>
              Reload
            </Button>
            <Button variant="secondary" onClick={() => void regenerateManifest()} disabled={loading || saving}>
              Regenerate manifest
            </Button>
            <Button onClick={() => void saveManifest()} disabled={loading || saving || data == null}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error != null && <p role="alert" className="mb-4 rounded-lg border border-rose-900 bg-rose-950/50 p-3 text-sm text-rose-200">{error}</p>}
          {notice != null && <p role="status" className="mb-4 rounded-lg border border-cyan-900 bg-cyan-950/40 p-3 text-sm text-cyan-100">{notice}</p>}
          {loading ? (
              <p className="py-10 text-center text-slate-400">Loading image catalog…</p>
          ) : draftGifs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-700 p-10 text-center">
              <p className="font-semibold">No images in this manifest</p>
              <p className="mt-2 text-sm text-slate-400">Regenerate after images have been copied into this environment bucket.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {draftGifs.map((gif) => (
                <article key={gif.id} className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/80">
                  <div className="flex aspect-video items-center justify-center bg-slate-950">
                    <img src={gif.url} alt="" loading="lazy" className="h-full w-full object-contain" />
                  </div>
                  <div className="flex items-center justify-between gap-3 p-4">
                    <Badge className={gif.enabled ? 'border-emerald-700 bg-emerald-950 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-300'}>
                      {gif.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <Button variant={gif.enabled ? 'secondary' : 'default'} onClick={() => toggleGif(gif.id)}>
                      {gif.enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
          {data?.updatedAt != null && <p className="mt-5 text-xs text-slate-500">Last updated {new Date(data.updatedAt).toLocaleString()}</p>}
        </CardContent>
      </Card>
    </main>
  )
}
